import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { RootState } from '../../../states/containers/store'
import { format, getYear, nextMonth, nowDate, prevMonth } from '../../../utils/date'
import { getGetPrams } from '../../../utils/url'

interface Props {
  date: string
  changeCallback: (date: string) => void
  startMonth?: string
  disabled?: boolean
  noLimit?: boolean
  navigation?: boolean
  empty?: boolean
}

interface GetParams {
  date: string
}

const DateModule: FC<Props> = (props) => {
  const { t } = useTranslation()
  const location = useLocation()

  const {
    commonPart,
    task,
  } = useSelector((state: RootState) => state)

  const {
    date,
    changeCallback,
    startMonth,
    disabled,
    noLimit,
    navigation,
    empty,
  } = props

  const [startDate, setStartDate] = useState<string>()
  const [isCalendarDisplay, setIsCalendarDisplay] = useState(false)
  const [isClickable, setIsClickable] = useState(true)
  const [targetYear, setTargetYear] = useState<number>(Number(nowDate("YYYY")))

  const month = t('time.date.formats.monthName').split(' ').map((v) => v)

  const startMonthNum = useMemo(() => {
    if (startMonth) {
      return Number(startMonth)
    }
    return Number(format(commonPart.company?.created_at, 'YYYYMM'))
  }, [startMonth])

  const handleChangeMonth = (value: string | null) => {
    setIsCalendarDisplay(false)
    setStartDate(value ? format(value) : undefined)
    changeCallback(value || "")
  }

  useEffect(() => {
    if (empty) {
      setStartDate(date ? format(date) : undefined)
      setTargetYear(date ? Number(format(date, "YYYY")) : Number(nowDate("YYYY")))
      return
    }
    const getParams = getGetPrams(location.search) as GetParams
    if (!getParams.date) {
      setStartDate(date)
      setTargetYear(Number(format(date, "YYYY")))
    } else {
      setStartDate(format(getParams.date))
      setTargetYear(Number(nowDate("YYYY")))
    }
  }, [location, date])

  useEffect(() => {
    const html = document.querySelector('html')
    if (!html) return
    if (isCalendarDisplay) {
      html.classList.add('modal_open')
    } else {
      html.classList.remove('modal_open')
    }
  }, [isCalendarDisplay])

  useEffect(() => {
    setIsClickable(!task.isLoading)
  }, [task.isLoading])

  return (
    <>
      <div className="calendar_text">
        <button
          type="button"
          className="display button"
          onClick={() => setIsCalendarDisplay(true)}
          disabled={!isClickable || disabled}
        >
          {
            startDate ? (
              <>
                {format(startDate, "YYYY年MM月")}
              </>
            ) : (
              <>
                指定なし
              </>
            )
          }
          <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isCalendarDisplay && 'open'}`} />
        </button>
        {
          navigation && (
            <>
              <button
                type="button"
                className="prev button"
                onClick={() => handleChangeMonth(prevMonth(startDate || nowDate(), 'YYYY-MM-01'))}
                disabled={
                  (!noLimit && (
                    !commonPart.company?.created_at
                    || Number(prevMonth(startDate || nowDate(), 'YYYYMM')) < startMonthNum
                  )) || disabled || !isClickable
                }
              >
                <FontAwesomeIcon icon={['fas', 'angle-left']} />
              </button>
              <button
                type="button"
                className="next button"
                onClick={() => handleChangeMonth(nextMonth(startDate || nowDate(), 'YYYY-MM-01'))}
                disabled={
                  (!noLimit && (
                    !commonPart.company?.created_at
                    || Number(nextMonth(startDate || nowDate(), 'YYYYMM')) > Number(nowDate('YYYYMM'))
                  )) || disabled || !isClickable
                }
              >
                <FontAwesomeIcon icon={['fas', 'angle-right']} />
              </button>
            </>
          )
        }
      </div>
      <div className={`calendar_box ${isCalendarDisplay || 'none'}`}>
        <div
          className="range"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            className="text"
            style={{
              margin: 0,
            }}
          >
            {t(`range.calendar.header_text.monthly`)}
          </div>
          {
            empty && (
              <>
                <button
                  type="button"
                  onClick={() => handleChangeMonth(null)}
                  style={{
                    border: "solid 1px #CCCCCC",
                    borderRadius: "4px",
                  }}
                >
                  指定なし
                </button>
              </>
            )
          }
        </div>
        <div className="monthly">
          <div className="select_month">
            <div>
              <button
                type="button"
                onClick={() => setTargetYear(targetYear - 1)}
                disabled={!noLimit && Number(format(`${startMonthNum}01`, 'YYYY')) >= targetYear}
              >
                <FontAwesomeIcon icon={['fas', 'angle-left']} />
              </button>
            </div>
            <div>
              {`${targetYear}${t('time.time.short.year')}`}
            </div>
            <div>
              <button
                type="button"
                onClick={() => setTargetYear(targetYear + 1)}
                disabled={!noLimit && getYear() <= targetYear}
              >
                <FontAwesomeIcon icon={['fas', 'angle-right']} />
              </button>
            </div>
          </div>
          {
            month.map((v, i) => {
              const targetYearDate = String(targetYear) + (`0${String(i + 1)}`).slice(-2)
              const isClickableMonth = noLimit || (
                Number(nowDate('YYYYMM')) >= Number(targetYearDate)
                && startMonthNum <= Number(targetYearDate)
              )
              const isSelected = format(startDate, 'YYYYMM') === targetYearDate
              return (
                <div className="monthly_button" key={v}>
                  <button
                    type="button"
                    className={isSelected ? "selected" : ""}
                    onClick={() => handleChangeMonth(`${String(targetYear)}-${(`0${String(i + 1)}`).slice(-2)}-01`)}
                    disabled={!isClickableMonth}
                  >
                    {`${v}${t('time.time.short.month')}`}
                  </button>
                </div>
              )
            })
          }
        </div>
      </div>
      <div className={`close_screen gray ${isCalendarDisplay || 'none'}`} role="presentation" onClick={() => setIsCalendarDisplay(false)} />
    </>
  )
}

DateModule.defaultProps = {
  startMonth: "",
  disabled: false,
  noLimit: false,
  navigation: true,
  empty: false,
}

export default DateModule
